import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AppUser } from '../types';
import { xpProgress } from '../utils/xp';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

interface LeaderboardViewProps {
  users?: AppUser[]; // legacy prop; authoritative ranks load from the server
  currentUserUid?: string;
  currentUserId?: string;
  currentUid?: string;
  isDarkMode?: boolean;
  onOpenUserProfile?: (userId: string) => void;
}

const LEADERBOARD_PAGE_SIZE = 10;

function getUserXP(user: AppUser): number {
  const raw = Number((user as any)?.xp ?? 0);
  return Number.isFinite(raw) ? Math.max(0, Math.floor(raw)) : 0;
}

function getUserLevel(user: AppUser): number {
  const explicit = Number((user as any)?.level);
  if (Number.isFinite(explicit) && explicit > 0) return Math.floor(explicit);
  return xpProgress(getUserXP(user)).level;
}

function getDisplayName(user: AppUser, fallback: string): string {
  return (
    (user as any)?.displayName ||
    (user as any)?.username ||
    (user as any)?.name ||
    fallback
  );
}

function getAvatarInitial(user: AppUser, fallback: string): string {
  const name = String(getDisplayName(user, fallback)).trim();
  return (name[0] || 'A').toUpperCase();
}

function getAvatarUrl(user: AppUser): string {
  return String(
    (user as any)?.avatar ||
      (user as any)?.avatarUrl ||
      (user as any)?.photoURL ||
      (user as any)?.photoUrl ||
      (user as any)?.profilePhotoUrl ||
      (user as any)?.profilePhoto ||
      (user as any)?.profileImageUrl ||
      (user as any)?.imageUrl ||
      (user as any)?.picture ||
      '',
  ).trim();
}

function getUserId(user: AppUser): string {
  return String(
    (user as any)?.uid ||
      (user as any)?.id ||
      (user as any)?.userId ||
      (user as any)?.authorId ||
      '',
  ).trim();
}

function getRankBadge(rank: number): string {
  if (rank === 1) return '🏆';
  if (rank === 2) return '🥈';
  if (rank === 3) return '🥉';
  return `#${rank}`;
}

function formatXp(xp: number): string {
  try {
    return xp.toLocaleString();
  } catch {
    return String(xp);
  }
}

function useResolvedDarkMode(isDarkMode?: boolean) {
  const [domDark, setDomDark] = useState(false);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const readDark = () => {
      const root = document.documentElement;
      const body = document.body;
      setDomDark(
        root.classList.contains('dark') ||
          root.dataset.theme === 'dark' ||
          body?.dataset?.theme === 'dark',
      );
    };

    readDark();

    const observer = new MutationObserver(readDark);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme'],
    });

    if (document.body) {
      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ['class', 'data-theme'],
      });
    }

    return () => observer.disconnect();
  }, []);

  return typeof isDarkMode === 'boolean' ? isDarkMode : domDark;
}

function getRankTone(rank: number, dark: boolean): React.CSSProperties {
  if (rank === 1) {
    return {
      background: dark ? '#D8B64C' : '#F5CF58',
      color: '#3B2600',
      borderColor: dark ? 'rgba(255,236,153,0.65)' : 'rgba(173,121,0,0.22)',
      boxShadow: dark ? '0 10px 24px rgba(250,204,21,0.20)' : '0 10px 22px rgba(180,130,8,0.16)',
    };
  }

  if (rank === 2) {
    return {
      background: dark ? '#B9C5D5' : '#D9E0E9',
      color: '#1F2A3A',
      borderColor: dark ? 'rgba(226,232,240,0.70)' : 'rgba(100,116,139,0.22)',
      boxShadow: dark ? '0 10px 24px rgba(203,213,225,0.14)' : '0 10px 22px rgba(100,116,139,0.14)',
    };
  }

  if (rank === 3) {
    return {
      background: dark ? '#C98654' : '#E9A06C',
      color: '#2E1304',
      borderColor: dark ? 'rgba(254,215,170,0.58)' : 'rgba(194,65,12,0.20)',
      boxShadow: dark ? '0 10px 24px rgba(251,146,60,0.16)' : '0 10px 22px rgba(194,65,12,0.12)',
    };
  }

  return {
    background: dark ? 'rgba(148,166,201,0.08)' : 'rgba(255,255,255,0.82)',
    color: dark ? '#F8EFD9' : '#3E2E1E',
    borderColor: dark ? 'rgba(255,255,255,0.16)' : 'rgba(62,46,30,0.12)',
    boxShadow: dark ? '0 10px 22px rgba(0,0,0,0.20)' : '0 10px 20px rgba(62,46,30,0.08)',
  };
}

const LeaderboardView: React.FC<LeaderboardViewProps> = ({
  currentUserUid,
  currentUserId,
  currentUid,
  isDarkMode,
  onOpenUserProfile,
}) => {
  const { t } = useTranslation();
  const dark = useResolvedDarkMode(isDarkMode);
  const me = currentUserUid || currentUserId || currentUid;
  const [page, setPage] = useState(1);
  const [entries, setEntries] = useState<AppUser[]>([]);
  const [cursorStack, setCursorStack] = useState<Array<string | null>>([null]);
  const [nextAfterUid, setNextAfterUid] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const activeCursor = cursorStack[page - 1] ?? null;

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const callable = httpsCallable(functions, 'getLeaderboardPage');
        const result = await callable({
          pageSize: LEADERBOARD_PAGE_SIZE,
          ...(activeCursor ? { afterUid: activeCursor } : {}),
        });
        if (cancelled) return;

        const data = (result.data || {}) as any;
        const rows = Array.isArray(data?.entries) ? data.entries : [];
        setEntries(rows as AppUser[]);
        setNextAfterUid(data?.nextAfterUid ? String(data.nextAfterUid) : null);
        setHasMore(data?.hasMore === true);
      } catch (error: any) {
        if (cancelled) return;
        console.error('[LeaderboardView] getLeaderboardPage failed', error);
        setEntries([]);
        setNextAfterUid(null);
        setHasMore(false);
        setLoadError('Ranks are temporarily unavailable.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [activeCursor]);

  const visibleRanked = useMemo(() => {
    const offset = (page - 1) * LEADERBOARD_PAGE_SIZE;
    return entries.map((user, index) => ({
      user,
      rank: offset + index + 1,
      isMe: !!me && ((user as any).uid === me || (user as any).id === me),
    }));
  }, [entries, me, page]);

  const shellStyle: React.CSSProperties = {
    color: dark ? '#F2F6FC' : '#34271A',
    transition: 'color 220ms ease',
  };

  const emptyCardStyle: React.CSSProperties = {
    border: `1px solid ${dark ? 'rgba(255,255,255,0.14)' : 'rgba(62,46,30,0.12)'}`,
    background: dark
      ? '#101827'
      : 'linear-gradient(180deg, rgba(255,253,248,0.94), rgba(255,246,224,0.86))',
    color: dark ? '#F2F6FC' : '#34271A',
    boxShadow: dark ? '0 16px 34px rgba(0,0,0,0.28)' : '0 14px 30px rgba(62,46,30,0.08)',
    transition: 'background 220ms ease, border-color 220ms ease, color 220ms ease, box-shadow 220ms ease',
  };

  if (isLoading) {
    return (
      <div style={shellStyle}>
        <div className="rounded-[18px] p-4 text-center" style={emptyCardStyle}>
          <p className="text-sm font-black">{t('leaderboard.loading', 'Loading ranks…')}</p>
        </div>
      </div>
    );
  }

  if (loadError || !visibleRanked.length) {
    return (
      <div style={shellStyle}>
        <div className="rounded-[18px] p-4 text-center" style={emptyCardStyle}>
          <div
            className="mx-auto grid h-12 w-12 place-items-center rounded-[12px] text-xl"
            style={{
              border: `1px solid ${dark ? 'rgba(255,255,255,0.14)' : 'rgba(62,46,30,0.12)'}`,
              background: dark ? 'rgba(148,166,201,0.08)' : 'rgba(255,255,255,0.78)',
            }}
          >
            🏆
          </div>
          <p className="mt-3 text-sm font-black">{loadError || t('leaderboard.emptyTitle', 'Leaderboard warming up')}</p>
          <p
            className="mx-auto mt-1 max-w-[260px] text-xs font-bold leading-relaxed"
            style={{ color: dark ? '#D8C8F6' : '#6F5A41' }}
          >
            {t('leaderboard.emptyBody', 'XP from catches, challenges, and community activity will show here.')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" style={shellStyle}>
      {visibleRanked.map(({ user, rank, isMe }) => {
        const xp = getUserXP(user);
        const level = getUserLevel(user);
        const fallback = t('common.angler', 'Angler');
        const name = getDisplayName(user, fallback);
        const avatarUrl = getAvatarUrl(user);
        const userId = getUserId(user);
        const canOpenProfile = Boolean(onOpenUserProfile && userId);

        const rowStyle: React.CSSProperties = {
          border: `1px solid ${
            isMe
              ? dark
                ? 'rgba(138,112,242,0.38)'
                : 'rgba(124,111,247,0.34)'
              : dark
              ? 'rgba(255,255,255,0.13)'
              : 'rgba(62,46,30,0.11)'
          }`,
          background: isMe
            ? dark
              ? '#151F31'
              : 'linear-gradient(135deg, rgba(247,244,255,0.98), rgba(255,249,229,0.94))'
            : dark
            ? '#101827'
            : 'linear-gradient(135deg, rgba(255,255,255,0.94), rgba(255,248,232,0.86))',
          color: dark ? '#F2F6FC' : '#34271A',
          boxShadow: isMe
            ? dark
              ? '0 12px 28px rgba(0,0,0,0.24), 0 0 0 1px rgba(112,87,232,0.18) inset'
              : '0 14px 30px rgba(124,111,247,0.10), 0 0 0 2px rgba(124,111,247,0.08) inset'
            : dark
            ? '0 12px 26px rgba(0,0,0,0.22)'
            : '0 12px 26px rgba(62,46,30,0.07)',
          transition: 'background 220ms ease, border-color 220ms ease, color 220ms ease, box-shadow 220ms ease, transform 180ms ease',
        };

        const avatarStyle: React.CSSProperties = {
          border: `1px solid ${dark ? 'rgba(255,255,255,0.15)' : 'rgba(62,46,30,0.10)'}`,
          background: dark ? 'rgba(148,166,201,0.08)' : 'rgba(255,255,255,0.82)',
          color: dark ? '#E6ECF5' : '#4B3520',
          boxShadow: dark ? 'inset 0 1px 0 rgba(255,255,255,0.08)' : '0 8px 18px rgba(62,46,30,0.06)',
        };

        const chipStyle: React.CSSProperties = {
          background: dark ? 'rgba(148,166,201,0.08)' : 'rgba(62,46,30,0.06)',
          color: dark ? '#C7D1E2' : '#604A31',
          border: `1px solid ${dark ? 'rgba(255,255,255,0.10)' : 'rgba(62,46,30,0.07)'}`,
        };

        return (
          <div
            key={getUserId(user) || rank}
            role={canOpenProfile ? 'button' : undefined}
            tabIndex={canOpenProfile ? 0 : undefined}
            onClick={() => {
              if (canOpenProfile) onOpenUserProfile?.(userId);
            }}
            onKeyDown={(event) => {
              if (!canOpenProfile) return;
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onOpenUserProfile?.(userId);
              }
            }}
            className={`rounded-[16px] p-3 sm:p-3.5 ${canOpenProfile ? 'cursor-pointer hover:-translate-y-0.5 active:translate-y-px' : ''}`}
            style={rowStyle}
          >
            <div className="flex items-center gap-3">
              <div
                className="grid h-12 w-12 shrink-0 place-items-center rounded-[12px] border text-xl font-black"
                style={getRankTone(rank, dark)}
                title={`Rank ${rank}`}
              >
                {getRankBadge(rank)}
              </div>

              <button
                type="button"
                disabled={!canOpenProfile}
                onClick={(event) => {
                  event.stopPropagation();
                  if (canOpenProfile) onOpenUserProfile?.(userId);
                }}
                className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-[12px] text-sm font-black transition enabled:hover:-translate-y-0.5 enabled:active:translate-y-px disabled:cursor-default"
                style={avatarStyle}
                aria-label={canOpenProfile ? `Open ${name}'s public profile` : name}
              >
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  getAvatarInitial(user, fallback)
                )}
              </button>

              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-center gap-2">
                  <button
                    type="button"
                    disabled={!canOpenProfile}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (canOpenProfile) onOpenUserProfile?.(userId);
                    }}
                    className="min-w-0 truncate text-left text-[15px] font-black transition enabled:hover:underline disabled:cursor-default"
                    style={{ color: dark ? '#F2F6FC' : '#2F2418' }}
                  >
                    {name}
                  </button>

                  {isMe ? (
                    <span
                      className="shrink-0 rounded-[10px] px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.16em]"
                      style={{
                        background: dark ? 'rgba(112,87,232,0.16)' : 'rgba(124,111,247,0.12)',
                        border: `1px solid ${dark ? 'rgba(112,87,232,0.28)' : 'rgba(124,111,247,0.22)'}`,
                        color: dark ? '#D9D3FF' : '#5D4DE6',
                      }}
                    >
                      {t('leaderboard.you', 'You')}
                    </span>
                  ) : null}
                </div>

                <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px] font-black">
                  <span className="rounded-[10px] px-2 py-0.5" style={chipStyle}>
                    🎚️ Level {level}
                  </span>
                  <span className="rounded-[10px] px-2 py-0.5" style={chipStyle}>
                    ⚡ {formatXp(xp)} XP
                  </span>
                  <span className="rounded-[10px] px-2 py-0.5" style={chipStyle}>
                    {rank <= 3 ? 'Podium' : 'Rank'} {rank}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {page > 1 || hasMore ? (
        <div
          className="flex items-center justify-between gap-3 rounded-[14px] px-3 py-2.5"
          style={{
            border: `1px solid ${dark ? 'rgba(255,255,255,0.12)' : 'rgba(62,46,30,0.10)'}`,
            background: dark ? 'rgba(255,255,255,0.055)' : 'rgba(255,255,255,0.72)',
          }}
        >
          <button
            type="button"
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            disabled={page <= 1}
            className="min-h-9 rounded-[11px] px-3 text-[12px] font-black transition disabled:cursor-default disabled:opacity-35"
            style={{
              border: `1px solid ${dark ? 'rgba(255,255,255,0.12)' : 'rgba(62,46,30,0.10)'}`,
              background: dark ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.84)',
              color: dark ? '#C7D1E2' : '#604A31',
            }}
          >
            ‹ Prev
          </button>

          <div className="text-center">
            <div className="text-[11px] font-black" style={{ color: dark ? '#F2F6FC' : '#34271A' }}>
              Page {page}
            </div>
            <div className="mt-0.5 text-[10px] font-bold" style={{ color: dark ? '#D8C8F6' : '#6F5A41' }}>
              Server-ranked · {LEADERBOARD_PAGE_SIZE} per page
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              if (!hasMore || !nextAfterUid) return;
              setCursorStack((current) => {
                const next = current.slice(0, page);
                next[page] = nextAfterUid;
                return next;
              });
              setPage((current) => current + 1);
            }}
            disabled={!hasMore || !nextAfterUid}
            className="min-h-9 rounded-[11px] px-3 text-[12px] font-black transition disabled:cursor-default disabled:opacity-35"
            style={{
              border: `1px solid ${dark ? 'rgba(255,255,255,0.12)' : 'rgba(62,46,30,0.10)'}`,
              background: dark ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.84)',
              color: dark ? '#C7D1E2' : '#604A31',
            }}
          >
            Next ›
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default LeaderboardView;
